import React, { useState, useEffect, useRef } from 'react';
import '../styles/Terminal.css';
import FileSystem from '../filesystem';
import fileSystemData from '../filesystem/data';

const Terminal = () => {
  const [inputValue, setInputValue] = useState('');
  const [output, setOutput] = useState([]);
  const [prompt, setPrompt] = useState('user@nyth.club:~$');
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [clientIP, setClientIP] = useState('Loading...');

  const [lastTabTime, setLastTabTime] = useState(0);
  const [lastTabText, setLastTabText] = useState('');

  const [runningCommand, setRunningCommand] = useState(null);

  // Initialize file system
  const [fs] = useState(() => {
    const fileSystem = new FileSystem().loadFromJSON(fileSystemData);
    // Set initial directory to /home/user
    fileSystem.cd('/home/user');
    return fileSystem;
  });

  // Track current path for reactivity
  const [currentPath, setCurrentPath] = useState('/home/user');

  const inputRef = useRef(null);
  const terminalRef = useRef(null);

  // Fetch client IP address on component mount
  useEffect(() => {
    const fetchIP = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        setClientIP(data.ip);
      } catch (error) {
        console.error('Error fetching IP:', error);
        setClientIP('Unable to determine');
      }
    };

    fetchIP();
  }, []);

  // Initialize terminal output
  useEffect(() => {
    const neofetchHtml = generateNeofetchHtml();
    setOutput([
      { text: neofetchHtml, type: 'output', isHtml: true },
      { text: '\nType "help" to see available commands.', type: 'system' }
    ]);
  }, [clientIP]); // Re-run when clientIP changes

  // Update prompt when path changes
  useEffect(() => {
    let displayPath = currentPath;
    // Replace /home/user with ~ for display
    if (displayPath.startsWith('/home/user')) {
      displayPath = displayPath.replace('/home/user', '~');
    }
    setPrompt(`user@nyth.club:${displayPath}$`);
  }, [currentPath]);

  // Scroll to bottom when output changes
  useEffect(() => {
    inputRef.current.focus();

    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [output]);

  const generateNeofetchHtml = () => {
    const browser = navigator.userAgent;
    const browserName = browser.includes('Firefox') ? 'Firefox' :
        browser.includes('Chrome') ? 'Chrome' :
            browser.includes('Safari') ? 'Safari' :
                browser.includes('Edge') ? 'Edge' : 'Unknown Browser';

    const platform = navigator.platform || 'Unknown Platform';
    const language = navigator.language || 'en-US';
    const dateTime = new Date().toLocaleString();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const ipAddress = clientIP;

    const asciiLogo = [
      `████████████  ██████`,
      `████████████  ██████`,
      `████    ████    ████`,
      `████     ███    ████`,
      `████      ██    ████`,
      `████       █    ████`,
      `████            ████`,
      `████    █       ████`,
      `████    ██      ████`,
      `████    ███     ████`,
      `████    ████    ████`,
      `██████  ████████████`,
      `██████  ████████████`
    ];

    const systemInfo = [
      `<span class="neofetch-user">user@nyth.club</span>`,
      `<span class="neofetch-sep">--------------------------</span>`,
      `<span class="neofetch-label">OS:</span> Web Browser`,
      `<span class="neofetch-label">Browser:</span> ${browserName}`,
      `<span class="neofetch-label">Platform:</span> ${platform}`,
      `<span class="neofetch-label">IP Address:</span> ${ipAddress}`,
      `<span class="neofetch-label">Language:</span> ${language}`,
      `<span class="neofetch-label">Timezone:</span> ${timezone}`,
      `<span class="neofetch-label">Date/Time:</span> ${dateTime}`,
      `<span class="neofetch-label">Terminal:</span> nyth.club v1.0`,
      `<span class="neofetch-label">File system:</span> Enabled (readonly)`
    ];

    const rows = [];

    const maxLines = Math.max(asciiLogo.length, systemInfo.length);

    for (let i = 0; i < maxLines; i++) {
      const logoLine = i < asciiLogo.length ? asciiLogo[i] : '';
      const infoLine = i < systemInfo.length ? systemInfo[i] : '';

      rows.push(`<div><span class="neofetch-logo">${logoLine}</span>    ${infoLine}</div>`);
    }

    return rows.join('');
  };

  const handleTerminalClick = () => {
    inputRef.current.focus();
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      if (document.activeElement !== inputRef.current) {
        inputRef.current.focus();
      }
    }, 10);
  };

  const commands = {
    help: {
      name: 'help',
      description: 'Displays help for commands',
      usage: 'help [command]',
      longHelp: 'Without arguments, this command lists all available commands.\nWith a command name as an argument, it displays detailed information about that command.',
      autocomplete: (args) => {
        if (args.length <= 1) {
          return Object.keys(commands);
        }
        return [];
      },
      execute: (args) => {
        if (args.length === 0) {
          const cmdList = Object.keys(commands).map(cmd =>
              `${cmd.padEnd(10)} - ${commands[cmd].description}`
          ).join('\n');

          return `Available commands:\n\n${cmdList}\n\nFor details on a specific command: help [command]`;
        } else {
          const cmdName = args[0];
          if (commands[cmdName]) {
            const cmd = commands[cmdName];
            return `${cmd.name} - ${cmd.description}\n\nUsage: ${cmd.usage}\n\n${cmd.longHelp}`;
          } else {
            return `Unknown command: ${cmdName}`;
          }
        }
      }
    },

    clear: {
      name: 'clear',
      description: 'Clears the terminal screen',
      usage: 'clear',
      longHelp: 'This command clears all previous output from the visible terminal area.',
      autocomplete: () => [],
      execute: () => {
        setOutput([]);
        return '';
      }
    },

    history: {
      name: 'history',
      description: 'Shows command history',
      usage: 'history [options]',
      longHelp: 'Displays the command history list with line numbers.\nOptions:\n  -c  clear the history list',
      autocomplete: (args) => {
        return ['-c'];
      },
      execute: (args) => {
        if (args.length > 0 && args[0] === '-c') {
          setHistory([]);
          return 'History cleared';
        }

        if (history.length === 0) {
          return 'No commands in history';
        }

        return history.map((cmd, index) =>
            `${(index + 1).toString().padStart(4)}  ${cmd}`
        ).join('\n');
      }
    },

    neofetch: {
      name: 'neofetch',
      description: 'Displays system information in a visually appealing way',
      usage: 'neofetch',
      longHelp: 'Displays system information about your browser environment in a neofetch-inspired format.',
      autocomplete: () => [],
      execute: () => {
        const htmlContent = generateNeofetchHtml();
        return { text: htmlContent, isHtml: true };
      }
    },

    ip: {
      name: 'ip',
      description: 'Shows your IP address',
      usage: 'ip',
      longHelp: 'Shows your current public IP address.',
      autocomplete: () => [],
      execute: () => {
        return `Your public IP address is: ${clientIP}`;
      }
    },

    shortcuts: {
      name: 'shortcuts',
      description: 'Displays available keyboard shortcuts',
      usage: 'shortcuts',
      longHelp: 'Lists all keyboard shortcuts available in the terminal.',
      autocomplete: () => [],
      execute: () => {
        return `Available keyboard shortcuts:

Ctrl+C  - Abort current command
Ctrl+L  - Clear the screen (same as 'clear' command)

Arrow Up/Down - Navigate through command history
Tab           - Autocomplete commands and arguments`;
      }
    },

    // File system commands
    pwd: {
      name: 'pwd',
      description: 'Print working directory',
      usage: 'pwd',
      longHelp: 'Prints the current working directory path.',
      autocomplete: () => [],
      execute: () => {
        return fs.pwd();
      }
    },

    cd: {
      name: 'cd',
      description: 'Change directory',
      usage: 'cd [directory]',
      longHelp: 'Changes the current working directory to the specified path.\nWithout arguments, it changes to the home directory (/home/user).',
      autocomplete: (args) => {
        if (args.length <= 1) {
          const listing = fs.ls();
          return listing ? listing.directories : [];
        }
        return [];
      },
      execute: (args) => {
        if (args.length === 0 || args[0] === '~') {
          // Default to home directory
          if (fs.cd('/home/user')) {
            setCurrentPath('/home/user');
            return '';
          } else {
            return 'Failed to change to home directory';
          }
        }

        if (fs.cd(args[0])) {
          // Update the current path after successful cd
          setCurrentPath(fs.pwd());
          return '';
        } else {
          return `cd: ${args[0]}: No such directory`;
        }
      }
    },

    ls: {
      name: 'ls',
      description: 'List directory contents',
      usage: 'ls [options] [directory]',
      longHelp: 'Lists the contents of a directory.\nOptions:\n  -l  use a long listing format\n  -a  include hidden files (files that begin with .)',
      autocomplete: (args) => {
        if (args.length === 0) return ['-l', '-a', '-la'];

        if (args.length === 1 && (args[0] === '-l' || args[0] === '-a' || args[0] === '-la')) {
          const listing = fs.ls();
          return listing ? [...listing.directories, ...listing.files] : [];
        }

        if (args.length <= 1) {
          const listing = fs.ls();
          return listing ? [...listing.directories, ...listing.files] : [];
        }

        return [];
      },
      execute: (args) => {
        let path = null;
        let showHidden = false;
        let longFormat = false;

        // Parse arguments
        for (const arg of args) {
          if (arg.startsWith('-')) {
            if (arg.includes('a')) showHidden = true;
            if (arg.includes('l')) longFormat = true;
          } else {
            path = arg;
          }
        }

        const listing = fs.ls(path);

        if (!listing) {
          return `ls: cannot access '${path}': No such file or directory`;
        }

        const { directories, files } = listing;

        if (longFormat) {
          let output = '';

          directories.forEach(dir => {
            if (!showHidden && dir.startsWith('.')) return;
            output += `drwxr-xr-x 2 user user 4096 Apr 13 12:00 ${dir}\n`;
          });

          files.forEach(file => {
            if (!showHidden && file.startsWith('.')) return;
            output += `-rw-r--r-- 1 user user 1024 Apr 13 12:00 ${file}\n`;
          });

          return output.trim();
        } else {
          // Simple format
          let items = [
            ...directories.filter(dir => showHidden || !dir.startsWith('.')).map(dir => `<span class="directory">${dir}/</span>`),
            ...files.filter(file => showHidden || !file.startsWith('.')).map(file => file)
          ];

          return items.length ? { text: items.join('  '), isHtml: true } : '';
        }
      }
    },

    cat: {
      name: 'cat',
      description: 'Concatenate and print files',
      usage: 'cat [file...]',
      longHelp: 'Concatenate FILE(s) to standard output.',
      autocomplete: (args) => {
        const listing = fs.ls();
        return listing ? listing.files : [];
      },
      execute: (args) => {
        if (args.length === 0) {
          return 'cat: missing file operand\nTry \'help cat\' for more information.';
        }

        const filePath = args[0];
        const content = fs.cat(filePath);

        if (content === null) {
          return `cat: ${filePath}: No such file or directory`;
        }

        if (content.error) {
          return `cat: ${content.message}`;
        }

        return content;
      }
    }
  };

  const processCommand = (cmdLine) => {
    if (!cmdLine.trim()) {
      return '';
    }

    const parts = cmdLine.trim().split(/\s+/);
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1);

    if (commands[cmd]) {
      return commands[cmd].execute(args);
    } else {
      return `${cmd}: command not found. Try 'help' for a list of available commands.`;
    }
  };

  const findCommonPrefix = (strings) => {
    if (strings.length === 0) return '';
    if (strings.length === 1) return strings[0];

    let i = 0;
    while (strings.every(s => s.length > i && s[i] === strings[0][i])) {
      i++;
    }

    return strings[0].substring(0, i);
  };

  const autocompleteInput = (isDoubleTap) => {
    const input = inputValue.trim();

    if (!input) return;

    const parts = input.split(/\s+/);
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1);
    const currentArg = parts.length > 1 ? parts[parts.length - 1] : '';

    let matches = [];

    if (parts.length === 1 && !inputValue.endsWith(' ')) {
      matches = Object.keys(commands).filter(command =>
          command.startsWith(cmd)
      );

      if (matches.length === 1 && !isDoubleTap) {
        setInputValue(matches[0] + ' ');
        return;
      } else if (matches.length > 1 && !isDoubleTap) {
        const commonPrefix = findCommonPrefix(matches);
        if (commonPrefix.length > cmd.length) {
          setInputValue(commonPrefix);
          return;
        }
      }

      if (isDoubleTap && matches.length > 0) {
        setOutput(prev => [
          ...prev,
          { text: `${prompt} ${input}`, type: 'input' },
          { text: matches.join('  '), type: 'output' }
        ]);
        return;
      }
    }
    else if (commands[cmd] && commands[cmd].autocomplete) {
      const suggestions = commands[cmd].autocomplete(args);

      if (isDoubleTap && inputValue.endsWith(' ')) {
        setOutput(prev => [
          ...prev,
          { text: `${prompt} ${input}`, type: 'input' },
          { text: suggestions.join('  '), type: 'output' }
        ]);
        return;
      }

      matches = suggestions.filter(suggestion =>
          suggestion.startsWith(currentArg)
      );

      if (matches.length === 1 && !isDoubleTap) {
        if (parts.length === 1) {
          setInputValue(cmd + ' ' + matches[0] + ' ');
        } else {
          const newInput = parts.slice(0, -1).join(' ') + ' ' + matches[0];
          setInputValue(newInput + ' ');
        }
        return;
      } else if (matches.length > 1 && !isDoubleTap) {
        const commonPrefix = findCommonPrefix(matches);
        if (commonPrefix.length > currentArg.length) {
          if (parts.length === 1) {
            setInputValue(cmd + ' ' + commonPrefix);
          } else {
            const newInput = parts.slice(0, -1).join(' ') + ' ' + commonPrefix;
            setInputValue(newInput);
          }
          return;
        }
      }

      if (isDoubleTap && matches.length > 0) {
        setOutput(prev => [
          ...prev,
          { text: `${prompt} ${input}`, type: 'input' },
          { text: matches.join('  '), type: 'output' }
        ]);
        return;
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const cmd = inputValue;

      if (cmd.trim().length > 0) {
        setHistory(prev => [...prev, cmd]);
        setHistoryIndex(-1);
      }

      const result = processCommand(cmd);

      const outputResult = typeof result === 'string'
          ? { text: result, type: 'output' }
          : { ...result, type: 'output' };

      setOutput(prev => [
        ...prev,
        { text: `<span class="terminal-prompt">${prompt}</span> ${cmd}`, type: 'input', isHtml: true },
        outputResult
      ]);

      setInputValue('');
      setLastTabTime(0);
      setLastTabText('');
    }
    else if (e.key === 'Tab') {
      e.preventDefault();

      const now = Date.now();
      const isDoubleTap = now - lastTabTime < 500 && inputValue === lastTabText;

      autocompleteInput(isDoubleTap);

      setLastTabTime(now);
      setLastTabText(inputValue);
    }
    else if (e.ctrlKey) {
      switch (e.key) {
        case 'c':
          e.preventDefault();
          if (runningCommand) {
            setRunningCommand(null);
          }
          setOutput(prev => [
            ...prev,
            { text: `^C`, type: 'input' }
          ]);
          setInputValue('');
          return;

        case 'l':
          e.preventDefault();
          commands.clear.execute();
          return;

        default:
          break;
      }
    }
    else if (e.key === 'ArrowUp') {
      e.preventDefault();

      if (history.length > 0) {
        if (historyIndex < history.length - 1) {
          const newIndex = historyIndex + 1;
          setHistoryIndex(newIndex);
          setInputValue(history[history.length - 1 - newIndex]);
        }
      }

      setLastTabTime(0);
    }
    else if (e.key === 'ArrowDown') {
      e.preventDefault();

      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInputValue(history[history.length - 1 - newIndex]);
      }
      else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInputValue('');
      }

      setLastTabTime(0);
    }
    else {
      if (![
        'Shift', 'Control', 'Alt', 'Meta', 'CapsLock',
        'Escape', 'PageUp', 'PageDown', 'End', 'Home',
        'ArrowLeft', 'ArrowRight'
      ].includes(e.key)) {
        setLastTabTime(0);
      }
    }
  };

  return (
      <div
          className="terminal-container"
          onClick={handleTerminalClick}
      >
        <div
            ref={terminalRef}
            className="terminal-output"
        >
          {output.map((line, i) => (
              line.isHtml ? (
                  <div
                      key={i}
                      className={`terminal-line terminal-line-${line.type}`}
                      dangerouslySetInnerHTML={{ __html: line.text }}
                  />
              ) : (
                  <div
                      key={i}
                      className={`terminal-line terminal-line-${line.type}`}
                  >
                    {line.text}
                  </div>
              )
          ))}
        </div>
        <div className="terminal-input-container">
          <span className="terminal-prompt">{prompt}</span>
          <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleInputBlur}
              className="terminal-input"
              autoFocus
          />
        </div>
      </div>
  );
};

export default Terminal;